import type { ReactNode } from 'react';
import { Table } from '@tanstack/react-table';

import { TableCell, TableRow } from '@/components/ui/table';
import { centsToPrice } from '@/utils/formatter';
import i18n from '@/utils/i18n';
import {
  type MwkEventOverviewRow,
  resolvedMwkEventOverviewCurrency,
} from './columns';

const PREFIX_COLUMN_IDS = new Set([
  'select',
  'eventName',
  'containerTypeId',
  'containerTypeName',
]);

export type EventOverviewTotals = {
  packed: number;
  amountsFullBoxes: number;
  leftOverAmounts: number;
  containers: number;
  picking: number;
  returnedSealed: number;
  countedBoxes: number;
  cleaningPriceDisplay: string;
};

export function aggregateMwkEventOverviewRows(
  rows: MwkEventOverviewRow[],
): EventOverviewTotals {
  const totalCleaningPrice = rows.reduce(
    (s, r) => s + (r.logisticPartnerCleaningPrice ?? 0),
    0,
  );
  const cleaningPriceCurrencies = [
    ...new Set(rows.map((r) => resolvedMwkEventOverviewCurrency(r))),
  ];
  const cleaningPriceDisplay =
    cleaningPriceCurrencies.length === 1
      ? centsToPrice(totalCleaningPrice, cleaningPriceCurrencies[0])
      : '—';

  return {
    packed: rows.reduce((s, r) => s + r.packedAfterCleaning, 0),
    amountsFullBoxes: rows.reduce((s, r) => s + r.amountsFullBoxes, 0),
    leftOverAmounts: rows.reduce((s, r) => s + (r.leftOverAmounts ?? 0), 0),
    containers: rows.reduce((s, r) => s + r.containerAmounts, 0),
    picking: rows.reduce((s, r) => s + (r.picking ?? 0), 0),
    returnedSealed: rows.reduce((s, r) => s + (r.returnedSealed ?? 0), 0),
    countedBoxes: rows.reduce((s, r) => s + (r.countedBoxes ?? 0), 0),
    cleaningPriceDisplay,
  };
}

/** One Excel row summarizing `rows` (same keys as per-row export). */
export function buildEventOverviewExcelTotalRow(
  rows: MwkEventOverviewRow[],
): Record<string, string | number> | null {
  if (rows.length === 0) {
    return null;
  }
  const t = aggregateMwkEventOverviewRows(rows);
  return {
    [i18n.t('EventOverview:event')]: i18n.t('EventOverview:total'),
    [i18n.t('EventOverview:containerTypeId')]: '',
    [i18n.t('EventOverview:containerTypeName')]: '',
    [i18n.t('EventOverview:packedAfterCleaning')]: t.packed,
    [i18n.t('EventOverview:amountsFullBoxes')]: t.amountsFullBoxes,
    [i18n.t('EventOverview:leftOverAmounts')]: t.leftOverAmounts,
    [i18n.t('EventOverview:containerAmounts')]: t.containers,
    [i18n.t('EventOverview:picking')]: t.picking,
    [i18n.t('EventOverview:returnedSealed')]: t.returnedSealed,
    [i18n.t('EventOverview:countedBoxes')]: t.countedBoxes,
    [i18n.t('EventOverview:logisticPartnerCleaningPrice')]:
      t.cleaningPriceDisplay,
  };
}

export function EventOverviewTableFooter({
  table,
}: {
  table: Table<MwkEventOverviewRow>;
}) {
  const pageRows = table
    .getPaginationRowModel()
    .rows.map((row) => row.original);
  const totals = aggregateMwkEventOverviewRows(pageRows);

  const visible = table.getVisibleLeafColumns();

  let prefixSpan = 0;
  for (const col of visible) {
    if (PREFIX_COLUMN_IDS.has(col.id)) {
      prefixSpan += 1;
    } else {
      break;
    }
  }

  const rest = visible.slice(prefixSpan);

  const cellByColumnId: Partial<Record<string, ReactNode>> = {
    packedAfterCleaning: totals.packed,
    amountsFullBoxes: totals.amountsFullBoxes,
    leftOverAmounts: totals.leftOverAmounts,
    containerAmounts: totals.containers,
    picking: totals.picking,
    returnedSealed: totals.returnedSealed,
    countedBoxes: totals.countedBoxes,
    logisticPartnerCleaningPrice: totals.cleaningPriceDisplay,
  };

  return (
    <TableRow className="tw:font-semibold tw:hover:bg-muted/50">
      {prefixSpan > 0 && (
        <TableCell colSpan={prefixSpan}>
          {i18n.t('EventOverview:total')}
        </TableCell>
      )}
      {rest.map((col) => (
        <TableCell key={col.id} className="tw:text-right">
          {cellByColumnId[col.id] ?? null}
        </TableCell>
      ))}
    </TableRow>
  );
}
